/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import {
  FilePicker,
  PickedFile,
  PickFilesOptions,
  PickFilesResult,
} from '@capawesome/capacitor-file-picker';
import { FILE_EXTENSIONS, UserFile, FILE_ERRORS } from './model/file.model';
import { FILE_LIMIT } from '../../constants/files';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  //update to use multiple directors based on a map
  public userFiles: UserFile[] = [];
  private DOCUMENT_STORAGE = 'documents';
  public isAfileOperationInProcess = false;

  constructor(private platform: Platform) {}

  public async loadFiles() {
    // Retrieve cached document array data
    try {
      const fileList = await Preferences.get({ key: this.DOCUMENT_STORAGE });
      this.userFiles = JSON.parse(fileList.value) || [];
    } catch (ignore) {
      this.userFiles = [];
    }

    // If running on the web...
    if (!this.platform.is('hybrid')) {
      // Display the document by reading into base64 format
      for (let userFile of this.userFiles) {
        // Read each saved document's data from the Filesystem
        try {
          const readFile = await Filesystem.readFile({
            path: userFile.filepath,
            directory: Directory.Data,
          });

          // Web platform only: Load the document as base64 data
          userFile.webviewPath = `data:application/pdf;base64,${readFile.data}`;
        } catch (ignore) {}
      }
    }
  }

  async addFile() {
    this.isAfileOperationInProcess = true;
    // Android will not work with both file extension restrictions and multiple selections.
    let selectedFiles: PickFilesResult;

    try {
      selectedFiles = await FilePicker.pickFiles({
        types: FILE_EXTENSIONS,
        multiple: false,
        readData: true,
      } as PickFilesOptions);
    } catch (ignore) {
      this.isAfileOperationInProcess = false;
      throw new Error(FILE_ERRORS.canceled.valueOf());
    }
    try {
      this.validateFile(selectedFiles);
    } catch (reThrow) {
      selectedFiles = undefined;
      this.isAfileOperationInProcess = false;
      throw reThrow;
    }
    let fileToReturn;
    for (let i = 0; i < selectedFiles.files.length; i++) {
      fileToReturn = await this.saveFile(
        selectedFiles.files[i].data,
        selectedFiles.files[i].name,
      );
      this.userFiles.unshift(fileToReturn);
    }

    Preferences.set({
      key: this.DOCUMENT_STORAGE,
      value: JSON.stringify(this.userFiles),
    });

    this.isAfileOperationInProcess = false;
    return fileToReturn;
  }

  async dropFile(droppedFile: File) {
    let selectedFile = droppedFile;
    try {
      this.validateDroppedFile(droppedFile);
    } catch (reThrow) {
      selectedFile = undefined;
      this.isAfileOperationInProcess = false;
      throw reThrow;
    }
    let fileToReturn;
    fileToReturn = await this.saveFile(selectedFile, selectedFile.name);
    this.userFiles.unshift(fileToReturn);

    Preferences.set({
      key: this.DOCUMENT_STORAGE,
      value: JSON.stringify(this.userFiles),
    });

    this.isAfileOperationInProcess = false;
    return fileToReturn;
  }

  validateFile(filesResult: PickFilesResult) {
    if (filesResult.files[0].size > FILE_LIMIT) {
      throw new Error(FILE_ERRORS.wrongSize.valueOf());
    }

    if (!FILE_EXTENSIONS.includes(filesResult.files[0].mimeType)) {
      throw new Error(FILE_ERRORS.wrongMime.valueOf());
    }
  }

  validateDroppedFile(file: File) {
    if (file.size > FILE_LIMIT) {
      throw new Error(FILE_ERRORS.wrongSize.valueOf());
    }

    if (!FILE_EXTENSIONS.includes(file.type)) {
      throw new Error(FILE_ERRORS.wrongMime.valueOf());
    }
  }

  private async saveFile(data: any, fileName: string) {
    // Write the file to the data directory
    // Need to make sure the data is B64 encoded before writting it, else the write may fail.
    // The Filesystem plugin works differntly based on platform, web need encdoing to avoid a bug, but we don't want to encode on mobile
    var savedFile;
    if (this.platform.is('hybrid')) {
      savedFile = await Filesystem.writeFile({
        path: fileName,
        data: data,
        directory: Directory.Data,
      });
    } else {
      savedFile = await Filesystem.writeFile({
        path: fileName,
        data: data,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
    }
    if (this.platform.is('hybrid')) {
      return {
        name: fileName,
        filepath: savedFile.uri,
        webviewPath: Capacitor.convertFileSrc(savedFile.uri),
        extension: savedFile.uri,
      } as UserFile;
    } else {
      return {
        name: fileName,
        filepath: fileName,
        webviewPath: Capacitor.convertFileSrc(savedFile.uri),
        extension: savedFile.uri,
      } as UserFile;
    }
  }
  // Delete document by removing it from reference data and the filesystem
  public async deleteFile(userFile: UserFile) {
    this.isAfileOperationInProcess = true;
    // Remove this document from the Photos reference data array
    this.userFiles.splice(this.userFiles.indexOf(userFile), 1);
    // Update documents array cache by overwriting the existing document array
    Preferences.set({
      key: this.DOCUMENT_STORAGE,
      value: JSON.stringify(this.userFiles),
    });
    if (this.platform.is('hybrid')) {
      await Filesystem.deleteFile({
        path: userFile.filepath,
      });
    } else {
      await Filesystem.deleteFile({
        path: userFile.filepath,
        directory: Directory.Data,
      });
    }
    this.isAfileOperationInProcess = false;
  }

  public async deleteFiles(UserFiles: UserFile[]) {
    for (let file of UserFiles) {
      try {
        await this.deleteFile(file);
      } catch (ignore) {}
    }
  }

  public async loadFileData(filepath: string) {
    var readFile;
    if (this.platform.is('hybrid')) {
      readFile = await Filesystem.readFile({
        path: filepath,
      });
    } else {
      readFile = await Filesystem.readFile({
        path: filepath,
        directory: Directory.Data,
      });
    }
    const dataToReturn = readFile.data;
    readFile = undefined;
    return dataToReturn;
  }

  convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });

  convertFileToBase64String = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(
          (reader.result as string).replace('data:', '').replace(/^.+,/, ''),
        );
        reader.onerror = reject;
      };
      reader.readAsDataURL(file);
    });

  async getPickedFileFromFilePicker(
    mimeTypeWhiteList: string[],
    maxSize: number,
  ): Promise<PickedFile> {
    let selectedFiles: PickFilesResult;
    try {
      selectedFiles = await FilePicker.pickFiles({
        types: mimeTypeWhiteList,
        limit: 1,
        readData: true,
      } as PickFilesOptions);
    } catch (ignore) {
      throw new Error(FILE_ERRORS.canceled.valueOf());
    }
    const file = selectedFiles.files[0];
    if (file.size > maxSize) {
      throw new Error(FILE_ERRORS.wrongSize.valueOf());
    }
    if (!mimeTypeWhiteList.includes(file.mimeType)) {
      throw new Error(FILE_ERRORS.wrongMime.valueOf());
    }
    return file;
  }

  // Helper function
  getUserFileFromPickedFile(pickedFile: PickedFile): UserFile {
    let extension = '';
    const splitMimeType = pickedFile.mimeType?.split('/');
    if (splitMimeType?.length > 0) {
      extension = splitMimeType[1];
    }
    return {
      name: pickedFile.name,
      filepath: pickedFile.name,
      webviewPath: this.platform.is('hybrid')
        ? Capacitor.convertFileSrc(pickedFile.path)
        : URL.createObjectURL(pickedFile.blob),
      mimeType: pickedFile.mimeType,
      extension,
      isLoading: true,
    } as UserFile;
  }

  async pickedFileToBlob(pickedFile: PickedFile) {
    // Decode Base64 string to binary
    const binaryString = atob(pickedFile.data);

    // Convert the binary to Uint8Array
    const u8arr = new Uint8Array(binaryString.length);

    // Populate Uint8Array with decoded data
    for (let i = 0; i < binaryString.length; i++) {
      u8arr[i] = binaryString.charCodeAt(i);
    }

    // Create Blob from Uint8Array
    const blob = new Blob([u8arr], { type: pickedFile.mimeType });

    // Create and return File object
    return new File([blob], pickedFile.name, { type: pickedFile.mimeType });
  }
}
